import React from 'react';
import {
  AimOutlined,
  AppleOutlined,
  CreditCardOutlined,
  GiftOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import { Slider, Switch } from 'antd';
import styled from 'styled-components';

import { FEATURES } from 'appConstants';
import { useFeatureFlags } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../../i18n/TModules';
import { PlainButton } from '../../../components/pageStyles';
import {
  hasNoPrizeRow,
  outcomeErrors,
  probabilityErrors,
  probabilityTotal
} from '../../adapters/game';
import { limitErrors } from '../../adapters/limits';
import SelectionTile, { TileRow } from '../../components/SelectionTile';
import { WizardEntry } from '../../components/WizardFrame';
import StepPanel from '../../kit/StepPanel';
import { Checkbox, RadioGroup } from '../../kit/controls';
import { TextField } from '../../kit/fields';
import { Divider, Form, Hint, Line, Words } from '../../kit/layout';
import { EnrolmentFields, LimitLine, ScheduleFields } from '../../kit/limitFields';
import { gamePrizeLine } from '../../review/reviewRows';
import {
  IWizardStepProps,
  newOutcome,
  nextStep,
  WizardGame,
  WizardGameType,
  WizardLimits,
  WizardState
} from '../../wizardModel';
import { tint, wz, wzType } from '../../wizardStyles';
import PrizeList from '../PrizeList';

const TW = `${TOrbit}.wizard`;
const TG = `${TW}.game`;
const TL = `${TW}.limits`;

const GAMES: { type: WizardGameType; flag: string; icon: React.ReactNode }[] = [
  { type: 'shake_the_tree', flag: FEATURES.SHAKE_THE_TREE.key, icon: <AppleOutlined /> },
  { type: 'hit_the_pinata', flag: FEATURES.HIT_THE_PINATA.key, icon: <GiftOutlined /> },
  { type: 'scratch_card', flag: FEATURES.SCRATCH_CARD.key, icon: <CreditCardOutlined /> },
  { type: 'plinko', flag: FEATURES.PLINKO_GAME.key, icon: <AimOutlined /> },
  { type: 'spin_the_wheel', flag: FEATURES.SPIN_THE_WHEEL.key, icon: <PieChartOutlined /> }
];

const setGame = (state: WizardState, patch: Partial<WizardGame>): WizardState => ({
  ...state,
  game: { ...state.game, ...patch }
});

const gameTitle = (
  state: WizardState,
  t: (key: string, options?: Record<string, unknown>) => string
) => t(`${TW}.setup.game`, { game: t(`${TG}.${state.game.gameType ?? 'shake_the_tree'}`) });

/** 6778:26453, Step 2/5: the game mechanic; each tile behind its game's own flag. */
export const GameTypeStep: React.FC<IWizardStepProps> = ({ state, update, back }) => {
  const { t } = useMultiLanguageTranslation();
  const flags = useFeatureFlags() as Record<string, boolean | undefined>;
  return (
    <WizardEntry
      title={t(`${TW}.create.game`)}
      step={2}
      onBack={back}
      question={t(`${TG}.question`)}
    >
      <TileRow>
        {GAMES.filter(game => flags[game.flag] === true).map(game => (
          <SelectionTile
            key={game.type}
            icon={game.icon}
            label={t(`${TG}.${game.type}`)}
            selected={state.game.gameType === game.type}
            onSelect={() =>
              update(current => {
                const picked = setGame(current, { gameType: game.type });
                return { ...picked, step: nextStep(picked) };
              })
            }
            testId="orbit-wizard-game-tile"
          />
        ))}
      </TileRow>
    </WizardEntry>
  );
};

const Section = styled.div`
  margin-top: 18px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 4px;
  color: ${wz.text};
  font-weight: 700;
  ${wzType.question}
`;

/** 6778:24944, Step 3/5: how prizes are awarded, and the outcomes. */
export const GameOutcomesStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const flags = useFeatureFlags();
  const { game } = state;
  const chooseYourReward = flags[FEATURES.CHOOSE_YOUR_REWARD.key] === true;
  const change = (patch: Partial<WizardGame>) => update(current => setGame(current, patch));

  return (
    <StepPanel
      step="outcomes"
      title={gameTitle(state, t)}
      question={t(`${TG}.modeQuestion`)}
      errors={outcomeErrors(game, t)}
      onNext={next}
      onBack={back}
    >
      {chooseYourReward ? (
        <Line>
          <RadioGroup<WizardGame['mode']>
            name="orbit-wizard-game-mode"
            label={t(`${TG}.modeQuestion`)}
            value={game.mode}
            options={[
              { value: 'random', label: t(`${TG}.random`) },
              { value: 'choice', label: t(`${TG}.choice`) }
            ]}
            onChange={mode => change({ mode })}
          />
        </Line>
      ) : null}
      <Section>
        <SectionTitle>{t(`${TG}.outcomes`)}</SectionTitle>
        <Hint>{t(`${TG}.outcomesHint`)}</Hint>
      </Section>
      <Section>
        <PrizeList
          addLabel={t(`${TG}.addOutcome`)}
          name="orbit-wizard-outcome"
          prizes={game.outcomes}
          eyebrow={n => t(`${TG}.outcome`, { n })}
          blank={newOutcome}
          rewardQuery={game.mode === 'choice' ? { has_tags: true } : undefined}
          onChange={outcomes => change({ outcomes })}
        />
      </Section>
      <Line>
        <Checkbox
          showLabel
          checked={hasNoPrizeRow(game)}
          disabled={game.mode === 'choice'}
          label={t(`${TG}.noPrize`)}
          onChange={noPrize => change({ noPrize })}
        />
      </Line>
    </StepPanel>
  );
};

// 6778:25220's probability table: outcome sentence, then a % input.
const Table = styled.div`
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
  overflow: hidden;
`;

const Row = styled.div<{ $head?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ $head }) => ($head ? '10px 20px' : '8px 20px')};
  background: ${({ $head }) => ($head ? wz.field : 'transparent')};
  color: ${({ $head }) => ($head ? wz.muted : wz.text)};
  ${({ $head }) =>
    $head ? `font-weight: 800; text-transform: uppercase; ${wzType.eyebrow}` : wzType.input}

  & + & {
    border-top: ${wz.line} solid ${wz.border};
  }

  > :last-child {
    display: inline-flex;
    align-items: center;

    > * + * {
      margin-left: 8px;
    }
  }
`;

const Total = styled.div<{ $ok: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
  color: ${({ $ok }) => ($ok ? wz.success : wz.danger)};
  ${wzType.label}

  strong {
    color: ${({ $ok }) => ($ok ? wz.success : wz.danger)};
    font-size: 16px;
  }
`;

const WinBox = styled.div`
  padding: 20px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 12px;
  background: ${wz.field};
`;

const WinValue = styled.div`
  color: ${wz.text};
  font-weight: 700;
  font-size: 22px;
  line-height: 27px;
`;

const Ends = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${wz.muted};
  ${wzType.label}
`;

/** 6778:25220 (random: % per outcome, live total) and 6778:25456 (choice: one win slider). */
export const GameProbabilityStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const { game } = state;
  const change = (patch: Partial<WizardGame>) => update(current => setGame(current, patch));
  const total = probabilityTotal(game);

  if (game.mode === 'choice') {
    return (
      <StepPanel
        step="probability"
        title={gameTitle(state, t)}
        question={t(`${TG}.winQuestion`)}
        errors={[]}
        onNext={next}
        onBack={back}
      >
        <WinBox>
          <Words>{t(`${TG}.win`)}</Words>
          <Hint>{` ${t(`${TG}.winHint`)}`}</Hint>
          <WinValue>{`${game.winProbability}%`}</WinValue>
          <Slider
            min={1}
            max={99}
            value={game.winProbability}
            onChange={(winProbability: number) => change({ winProbability })}
            tooltip={{ open: false }}
            aria-label={t(`${TG}.win`)}
          />
          <Ends>
            <span>0%</span>
            <span>100%</span>
          </Ends>
          <Words $muted>{t(`${TG}.winValue`, { n: game.winProbability })}</Words>
        </WinBox>
      </StepPanel>
    );
  }

  const percent = (label: string, value: string, onChange: (value: string) => void) => (
    <span>
      <TextField
        label={label}
        placeholder="0"
        inputMode="numeric"
        value={value}
        onChange={onChange}
      />
      <Words $muted>%</Words>
    </span>
  );

  return (
    <StepPanel
      step="probability"
      title={gameTitle(state, t)}
      question={t(`${TG}.probabilityQuestion`)}
      errors={probabilityErrors(game, t)}
      onNext={next}
      onBack={back}
    >
      <Hint>{t(`${TG}.probabilityHint`)}</Hint>
      <Section>
        <Table data-testid="orbit-wizard-probability-table">
          <Row $head>
            <span>{t(`${TG}.outcomeColumn`)}</span>
            <span>{t(`${TG}.probabilityColumn`)}</span>
          </Row>
          {game.outcomes.map((outcome, index) => {
            const label = gamePrizeLine(outcome, t);
            return (
              // Outcomes are their position, as on the step before.
              <Row key={index}>
                <span>{label}</span>
                {percent(label, outcome.probability, probability =>
                  change({
                    outcomes: game.outcomes.map((item, at) =>
                      at === index ? { ...item, probability } : item
                    )
                  })
                )}
              </Row>
            );
          })}
          {game.noPrize ? (
            <Row>
              <span>{t(`${TG}.noPrizeRow`)}</span>
              {percent(t(`${TG}.noPrizeRow`), game.noPrizeProbability, noPrizeProbability =>
                change({ noPrizeProbability })
              )}
            </Row>
          ) : null}
        </Table>
      </Section>
      <Total $ok={total === 100} aria-live="polite">
        <span>
          {total === 100
            ? ''
            : total < 100
            ? t(`${TG}.remaining`, { n: 100 - total })
            : t(`${TG}.over`, { n: total - 100 })}
        </span>
        <span>
          {`${t(`${TG}.total`)} `}
          <strong data-testid="orbit-wizard-probability-total">{`${total}%`}</strong>
        </span>
      </Total>
    </StepPanel>
  );
};

const SwitchLabel = styled.span`
  color: ${wz.secondary};
  ${wzType.body}
`;

const SwitchLine = styled(Line)`
  .ant-switch {
    background: ${wz.border};
  }

  .ant-switch-checked {
    background: ${wz.accent};
  }
`;

/** 6778:25676, Step 5/5: game tries, play limits, enrolment and the dates. */
export const GameLimitsStep: React.FC<IWizardStepProps> = ({ state, update, next, back }) => {
  const { t } = useMultiLanguageTranslation();
  const { limits } = state;
  const change = (patch: Partial<WizardLimits>) =>
    update(current => ({ ...current, limits: { ...current.limits, ...patch } }));

  return (
    <StepPanel
      step="limits"
      title={gameTitle(state, t)}
      question={t(`${TL}.question`)}
      errors={limitErrors(state, t)}
      onNext={next}
      onBack={back}
    >
      <Form>
        <SwitchLine>
          <Words>{t(`${TL}.autoIssue`)}</Words>
          <Switch
            checked={limits.autoIssue}
            aria-label={t(`${TL}.autoIssue`)}
            onChange={autoIssue => change({ autoIssue })}
          />
          <SwitchLabel>{t(`${TL}.${limits.autoIssue ? 'on' : 'off'}`)}</SwitchLabel>
        </SwitchLine>
        <LimitLine
          on={limits.totalOn}
          onToggle={totalOn => change({ totalOn })}
          leadIn={t(`${TL}.totalPlays`)}
          value={limits.total}
          onChange={total => change({ total })}
          unit={t(`${TL}.timesUnit`)}
        />
        <LimitLine
          on={limits.perUserOn}
          onToggle={perUserOn => change({ perUserOn })}
          leadIn={t(`${TL}.userPlays`)}
          value={limits.perUser}
          onChange={perUser => change({ perUser })}
          unit={t(`${TL}.timesUnit`)}
        />
        <LimitLine
          on={limits.userRateOn}
          onToggle={userRateOn => change({ userRateOn })}
          leadIn={t(`${TL}.userPlays`)}
          value={limits.userRate}
          onChange={userRate => change({ userRate })}
          unit={t(`${TL}.timesPer`)}
          period={{
            value: limits.userRatePeriod,
            onChange: userRatePeriod => change({ userRatePeriod })
          }}
        />
        <Divider />
        <EnrolmentFields limits={limits} onChange={change} withTime />
        <ScheduleFields limits={limits} onChange={change} />
      </Form>
    </StepPanel>
  );
};

const Pills = styled.div`
  display: inline-flex;
  margin-bottom: 18px;
  padding: 3px;
  border: ${wz.line} solid ${wz.border};
  border-radius: 999px;
  background: ${wz.field};
`;

const Pill = styled(PlainButton)<{ $on: boolean }>`
  padding: 7px 16px;
  border-radius: 999px;
  background: ${({ $on }) => ($on ? tint(wz.accent, 0.14) : 'transparent')};
  color: ${({ $on }) => ($on ? wz.accent : wz.secondary)};
  font-weight: 600;
  ${wzType.control}
`;

/**
 * 6778:25970's mode pills on the review. A switch changes what the odds mean, so it reopens the
 * probability step.
 */
export const GameModePills: React.FC<Pick<IWizardStepProps, 'state' | 'update'>> = ({
  state,
  update
}) => {
  const { t } = useMultiLanguageTranslation();
  const flags = useFeatureFlags();
  // The wheel binds a prize to each wedge: it has no prize mode (6985:12976).
  if (flags[FEATURES.CHOOSE_YOUR_REWARD.key] !== true || state.game.gameType === 'spin_the_wheel') {
    return null;
  }
  const options: { mode: WizardGame['mode']; label: string }[] = [
    { mode: 'random', label: t(`${TG}.random`) },
    { mode: 'choice', label: t(`${TG}.choiceShort`) }
  ];
  return (
    <Pills role="group" aria-label={t(`${TW}.review.prizeMode`)}>
      {options.map(option => (
        <Pill
          key={option.mode}
          type="button"
          aria-pressed={state.game.mode === option.mode}
          $on={state.game.mode === option.mode}
          onClick={() =>
            option.mode !== state.game.mode &&
            update(current => ({ ...setGame(current, { mode: option.mode }), step: 'probability' }))
          }
        >
          {option.label}
        </Pill>
      ))}
    </Pills>
  );
};
